import { PUBLIC_URL } from '../../config';
import { Accordion, Alert, Button, Form, FormGroup } from 'react-bootstrap';
import { AccordionToggle } from '../../components/AccordionToggle/AccordionToggle';
import LangWithComponents from '../../providers/LangProvider/components/LangWithComponents';
import { FEATURES, LINKS, RouterPaths } from '../../app/appConstants';
import React, { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext';
import {
    RespawnRealBannerStyled,
    RespawnRealBottomStyled,
    RespawnRealContentStyled,
    RespawnRealDepositInfoStyled,
    RespawnRealDescrStyled,
    RespawnRealEquityItemStyled,
    RespawnRealEquityStyled,
    RespawnRealMeanStyled,
    RespawnRealStyled,
    RespawnRealSuccessAmountContainerStyled,
    RespawnRealSuccessAmountStyled,
    RespawnRealSuccessAnimationWrapperStyled,
    RespawnRealSuccessBlurBackground1Styled,
    RespawnRealSuccessBlurBackground2Styled,
    RespawnRealSuccessContentContainerStyled,
    RespawnRealSuccessContentStyled,
    RespawnRealSuccessNextStatusStyled,
    RespawnRealSuccessStyled,
    RespawnRealTimerStyled,
    RespawnRealTopStyled,
} from './styled/RespawnRealStyled';
import amountFormat from '../../utils/amountFormat';
import { useDispatch, useSelector } from 'react-redux';
import { makeSelectorGetLiteTradeAccountMetrics } from '../LiteTrade/liteTradeSelectors';
import BinIcon from '../../components/icon/Icon';
import { getNewLifeAccountsAction, setNewLifeRespawnAction } from '../Bonus/bonusesActions';
import { getAppFeatures, getIsUserSelectedAccountTypeLive, getUserLiveAccount } from '../../app/appSelectors';
import appHistory from '../../app/appHistory';
import {
    getNewLifeAccounts,
    getNewLifeRespawnError,
    getNewLifeRespawnInProgress,
    getNewLifeRespawnSuccess,
} from '../Bonus/bonusesSelectors';
import { loadAccountTradeInfoAction, setUserSelectedAccountCode } from '../../app/appActions';
import RespawnRealPositions from './RespawnRealPositions';
import { InfoIcon } from '../../components/InfoIcon/InfoIcon';
import { NewLifeRefundAmountInfoStyled, NewLifeRefundDepositInfoStyled } from './styled/NewLifeAccountButtonStyled';
import moment from 'moment';
import { Loader } from '../../components/Loader/Loader';
import useSecondsCountdown from '../../hooks/useSecondsCountdown';
import { locationSlice } from '../Location/locationReducer';
import { LOCATION_COMPONENTS_SEARCH_PARAMS, LOCATION_NAMES } from '../Location/locationConstants';
import { getUsesStatusesSelector } from '../../app/userSelectors';
import IconUserStatus from '../UserStatus/IconUserStatus';
import Animation from '../Animation/Animation';
import { getIsLocationPlatformDesktopSelector } from '../Location/locationSelectors';
import classNames from 'classnames';
import { referralSlice } from '../Referral/referralReducer';
import { getReferralSelector } from '../Referral/referralSelectors';
import useCloseLocation from '../Location/hooks/useCloseLocation';

interface IRespawnRealProps {
    className?: string;
}

export default function RespawnReal(props: IRespawnRealProps) {
    const { className } = props;
    const { getLangPath } = useLangContext();
    const dispatch = useDispatch();
    const isDesktop = useSelector(getIsLocationPlatformDesktopSelector);
    const isLiveSelectedAccount = useSelector(getIsUserSelectedAccountTypeLive);
    const newLifeAccounts = useSelector(getNewLifeAccounts);
    const userLiveAccount = useSelector(getUserLiveAccount);
    const respawnInProgress = useSelector(getNewLifeRespawnInProgress);
    const respawnError = useSelector(getNewLifeRespawnError);
    const respawnSuccess = useSelector(getNewLifeRespawnSuccess);
    const metrics = useSelector(makeSelectorGetLiteTradeAccountMetrics(userLiveAccount?.name));
    const userStatuses = useSelector(getUsesStatusesSelector);
    const features = useSelector(getAppFeatures);
    const referral = useSelector(getReferralSelector);

    const [stepConfirm, setStepConfirm] = useState(false);

    const onCloseLocationModal = useCloseLocation({
        locationName: LOCATION_NAMES.RESPAWN,
        typeLocation: LOCATION_COMPONENTS_SEARCH_PARAMS.MODAL,
    });

    const selectedAccount = useMemo(() => {
            return newLifeAccounts.find(item => item.account === userLiveAccount?.name);
        }, [newLifeAccounts, userLiveAccount],
    );

    const {
        isInited: isTimeLeftToUseRespawnInited,
        formattedLabel: timeLeftToUseRespawnLabel,
    } = useSecondsCountdown(
        {
            endDate: selectedAccount?.validityAt,
            formatLabelParams: {
                days: true,
                hours: true,
                minutes: true,
                seconds: true,
            },
        });

    const onTopUpClickHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.DEPOSIT }));
    }, [dispatch]);

    const onGoTradingClickHandler = useCallback(() => {
        dispatch(locationSlice.actions.openLocationAction({ name: LOCATION_NAMES.HOME }));
    }, [dispatch]);

    const onRespawnClickHandler = useCallback(() => {
        setStepConfirm(true);
    }, []);

    const onRespawnCancelClickHandler = useCallback(() => {
        setStepConfirm(false);
    }, []);

    const onSubmitHandler = useCallback((e) => {
        e.preventDefault();

        const payload = {
            accountCode: selectedAccount.account,
            email: e.currentTarget.querySelector('[name=email-confirmation]').value,
        };

        if (features[FEATURES.REFERRAL] && referral) {
            if (isDesktop) {
                onCloseLocationModal();
            }

            dispatch(referralSlice.actions.setWarningResetProgress(payload));
            dispatch(locationSlice.actions.addLocationAction({
                name: LOCATION_NAMES.REFERRAL_WARNING_RESET_PROGRESS,
            }));
        } else {
            dispatch(setNewLifeRespawnAction(payload));
        }
    }, [dispatch, selectedAccount]);

    const isLoading = useMemo(() => {
        return !selectedAccount || !metrics;
    }, [metrics, selectedAccount]);

    const handleStartRealButtonClick: MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
        e.preventDefault();
        dispatch(setUserSelectedAccountCode(userLiveAccount?.name));
    }, [userLiveAccount, dispatch]);

    useEffect(() => {
        dispatch(getNewLifeAccountsAction());

        if (features[FEATURES.REFERRAL]) {
            dispatch(referralSlice.actions.getReferralAction());
        }
    }, [dispatch, features]);

    useEffect(() => {
        if (selectedAccount?.account && selectedAccount.isAvailable) {
            dispatch(loadAccountTradeInfoAction(selectedAccount?.account));
        }
    }, [dispatch, selectedAccount]);

    useEffect(() => {
        if (selectedAccount?.status === 'wagered' || selectedAccount?.reason === 'already.respawned') {
            appHistory.push(RouterPaths.main);
        }
    }, [respawnSuccess, selectedAccount]);

    const successAmountParams = useMemo(() => {
        const fullAmountString = amountFormat(selectedAccount?.respawned?.amount, 2, '$', false, { toFixed: true });
        const [price, decimal] = fullAmountString.split('.');
        return {
            decimal,
            price,
        };
    }, [selectedAccount]);

    const nextStatusWithRespawnParams = useMemo(() => {
        for (let i = 0; i < userStatuses.length; i++) {
            const currentStatus = userStatuses[i];
            if (currentStatus.status_state === 'plan' && currentStatus.gift_respawn) {
                const statusTitle = getLangPath(`user_statuses.statuses.${currentStatus.status_name}.title`);
                return {
                    status_name: currentStatus.status_name,
                    status_title: getLangPath('refill.real.status', { status_title: statusTitle }),
                };
            }
        }
        return null;
    }, [getLangPath, userStatuses]);

    const getInfoAboutDeposit = useCallback(() => {
        if (!selectedAccount) return null;
        return <RespawnRealDepositInfoStyled>
            {
                // @ts-ignore
                <InfoIcon size={24}>
                    <NewLifeRefundAmountInfoStyled>
                        <div className={'mb-3'}>
                            {getLangPath('bonus.new_life.max_deposit_info_tooltip', { amount: selectedAccount.maxRespawnAmount.toString() })}
                        </div>
                        {
                            !!selectedAccount.maxDeposit && <NewLifeRefundDepositInfoStyled>
                                <div>
                                    <div>{selectedAccount.maxDeposit.payment_name}</div>
                                    <div>{selectedAccount.maxDeposit.type}</div>
                                </div>
                                <div className={'text-right'}>
                                    <div>${amountFormat(selectedAccount.maxDeposit.amount)}</div>
                                    <div>{moment(selectedAccount.maxDeposit.completed_at).format(getLangPath('format.date_time'))}</div>
                                </div>
                            </NewLifeRefundDepositInfoStyled>
                        }
                    </NewLifeRefundAmountInfoStyled>
                </InfoIcon>
            }
        </RespawnRealDepositInfoStyled>;
    }, [getLangPath, selectedAccount]);

    if (respawnSuccess) {
        return <RespawnRealSuccessStyled className={classNames(className, 'success')}>
            <RespawnRealSuccessContentContainerStyled>
                <RespawnRealSuccessContentStyled>
                    <RespawnRealSuccessBlurBackground1Styled/>
                    <RespawnRealSuccessBlurBackground2Styled/>

                    <h2 className={'text-center'}>{getLangPath('refill.real.congratulations')}</h2>

                    <RespawnRealSuccessAmountContainerStyled>
                        <span>{getLangPath('refill.real.used_respawn')}</span>
                        <RespawnRealSuccessAmountStyled>
                            <span>{successAmountParams.price}</span>
                            <span className={'decimal'}>.{successAmountParams.decimal}</span>
                        </RespawnRealSuccessAmountStyled>
                    </RespawnRealSuccessAmountContainerStyled>

                    {
                        !!nextStatusWithRespawnParams && <RespawnRealSuccessNextStatusStyled>
                            <span className={'text-secondary'}>{getLangPath('refill.real.what_next')}</span>
                            <span className={'text-secondary'}>{getLangPath('refill.real.get_your_next_respawn')}</span>
                            <div className={'d-flex gap-2'}>
                                <b>{nextStatusWithRespawnParams.status_title}</b>
                                <IconUserStatus name={nextStatusWithRespawnParams.status_name}/>
                            </div>
                        </RespawnRealSuccessNextStatusStyled>
                    }

                    <RespawnRealSuccessAnimationWrapperStyled>
                        <Animation name={'Confetti'} loop={false}/>
                    </RespawnRealSuccessAnimationWrapperStyled>
                </RespawnRealSuccessContentStyled>
            </RespawnRealSuccessContentContainerStyled>
            <Button
                onClick={onGoTradingClickHandler}
                size={'lg'}
                variant={'primary'}
            >
                {getLangPath('refill.real.continue_trading')}
            </Button>
        </RespawnRealSuccessStyled>;
    }

    if (stepConfirm) {
        return <Form onSubmit={onSubmitHandler} className={'flex-1 d-flex'}>
            <RespawnRealStyled className={classNames(className, 'confirm')}>
                <Loader show={isLoading}/>
                <RespawnRealTopStyled>
                    <p dangerouslySetInnerHTML={{ __html: getLangPath('bonus.new_life.confirm_amount_info', { amount: selectedAccount.amountForRespawn.toString() }) }}/>
                    <Alert variant={'warning'} className={'f14 mb-4'}>
                        <BinIcon name={'IcAttention'} className="me-3"/>
                        <div dangerouslySetInnerHTML={{ __html: getLangPath('bonus.new_life.confirm_notice') }}/>
                    </Alert>
                    <p dangerouslySetInnerHTML={{ __html: getLangPath('bonus.new_life.confirm_enter_email') }}/>
                    <Form.Group className={'mb-3'}>
                        <Form.Control type={'text'} name={'email-confirmation'}
                                      readOnly={respawnInProgress}
                                      autoComplete={'off'}
                                      placeholder={getLangPath('bonus.new_life.confirm_email_placeholder')}
                                      isInvalid={!!respawnError}
                        />
                        {
                            respawnError && <FormGroup className={'mt-2'}>
                                <div className="text-danger">{getLangPath(respawnError)}</div>
                            </FormGroup>
                        }
                    </Form.Group>
                </RespawnRealTopStyled>
                <FormGroup className={'mx-3 mb-4'}>
                    <Button variant={'primary'}
                            type={'submit'}
                            size={'lg'}
                            disabled={!selectedAccount.isAvailable || respawnInProgress}
                            className={'w-100'}
                    >{getLangPath('bonus.new_life.confirm_button')}
                    </Button>
                    {
                        isDesktop && <Button variant={'tertiary'}
                                             size={'lg'}
                                             disabled={respawnInProgress}
                                             className={'w-100'}
                                             onClick={onRespawnCancelClickHandler}
                        >{getLangPath('lite_version.portfolio.cancel')}
                        </Button>
                    }
                </FormGroup>
            </RespawnRealStyled>
        </Form>;
    }

    return <RespawnRealStyled className={classNames(className, 'info')}>

        <RespawnRealTopStyled>
            <RespawnRealBannerStyled>
                <img src={`${PUBLIC_URL}/assets/bonus/bonus-respawn-dollar.png`} alt={' '}/>
            </RespawnRealBannerStyled>

            <RespawnRealDescrStyled>
                {getLangPath('refill.real.descr')}
            </RespawnRealDescrStyled>

            <RespawnRealMeanStyled>
                <Accordion>
                    <Accordion.Item eventKey={'0'}>
                        <AccordionToggle eventKey={'0'}>
                            <span>{getLangPath('bonus.new_life.what_mean.title')}</span>
                        </AccordionToggle>
                        <Accordion.Body>
                            <LangWithComponents
                                langPath={'refill.real.rules'}
                                langParams={{
                                    rules_link: LINKS.rulesNewLifeDoc,
                                }}
                            />
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </RespawnRealMeanStyled>
        </RespawnRealTopStyled>

        <RespawnRealBottomStyled>
            {
                !isLiveSelectedAccount ? <>
                    <Alert variant={'info'} className={'text-left'}>
                        <div className="d-flex">
                            <div className="d-flex me-2">
                                <BinIcon name={'IcInfoCircle'}/>
                            </div>
                            <div className={'f14'}>{getLangPath(`bonus.new_life.reasons.only_live`)}</div>
                        </div>
                    </Alert>
                    <Button variant={'primary'}
                            onClick={handleStartRealButtonClick}
                            size={'lg'}
                            className={'mt-3 w-100'}>{getLangPath('bonus.new_life.to_real')}
                    </Button>
                </> : !selectedAccount ? <>
                    <RespawnRealContentStyled>
                        <RespawnRealEquityStyled>
                            <div className="skeleton skeleton-br12"></div>
                        </RespawnRealEquityStyled>
                    </RespawnRealContentStyled>
                    <div className="skeleton skeleton-br12"></div>
                </> : <>
                    <RespawnRealContentStyled>
                        {isTimeLeftToUseRespawnInited && <RespawnRealTimerStyled>
                            <h2 className={'text-center mb-0'}>{timeLeftToUseRespawnLabel}</h2>
                            <span
                                className={'text-secondary f14'}>{getLangPath(`bonus.new_life.time_left_to_respawn`)}</span>
                        </RespawnRealTimerStyled>}
                        <RespawnRealEquityStyled>
                            {
                                !selectedAccount.isAvailable && (
                                    selectedAccount.maxRespawnAmount < selectedAccount.maxDeposit?.amount ?
                                        <RespawnRealEquityItemStyled>
                                            <div>{amountFormat(selectedAccount.maxRespawnAmount, 2, '$')}</div>
                                            <span>{getLangPath('refill.real.max_respawn')}</span>
                                        </RespawnRealEquityItemStyled>
                                        :
                                        <RespawnRealEquityItemStyled>
                                            <div>{amountFormat(selectedAccount.maxDeposit?.amount, 2, '$')}</div>
                                            <span>{getLangPath('refill.real.max_deposit')}</span>
                                        </RespawnRealEquityItemStyled>
                                )
                            }
                            <RespawnRealEquityItemStyled>
                                <div>{amountFormat(metrics?.equity, 2, '$')}</div>
                                <span>{getLangPath('refill.real.equity')}</span>
                            </RespawnRealEquityItemStyled>
                            {
                                selectedAccount.isAvailable && <>
                                    <RespawnRealEquityItemStyled>
                                        <BinIcon name={'IcArrowRight'}
                                                 size={16}
                                                 style={{
                                                     color: 'var(--color-white-60)',
                                                 }}
                                        />
                                    </RespawnRealEquityItemStyled>
                                    <RespawnRealEquityItemStyled>
                                        {getInfoAboutDeposit()}
                                        <div>{amountFormat(selectedAccount.amountForRespawn, 2, '$')}</div>
                                        <span>{getLangPath('refill.real.balance_after_respawn')}</span>
                                    </RespawnRealEquityItemStyled>
                                </>
                            }
                        </RespawnRealEquityStyled>
                        {
                            !!selectedAccount.reason && <Alert variant={'info'} className={'text-left'}>
                                <div className="d-flex">
                                    <div className="d-flex me-2">
                                        <BinIcon name={'IcInfoCircle'}/>
                                    </div>
                                    <div
                                        className={'f14'}>{getLangPath(`bonus.new_life.reasons.${selectedAccount.reason}`)}</div>
                                </div>
                            </Alert>
                        }
                        {
                            selectedAccount.isAvailable && <>
                                <RespawnRealPositions/>
                            </>
                        }
                    </RespawnRealContentStyled>
                    <FormGroup>
                        {
                            !selectedAccount.maxDeposit && <>
                                <Button variant={'primary'}
                                        onClick={onTopUpClickHandler}
                                        size={'lg'}
                                        className={'mt-3 w-100'}><BinIcon
                                    name={'IcPlusCircle'}/> {getLangPath('lite_version.home.top_up')}
                                </Button>
                            </>
                        }
                        {
                            !!selectedAccount.maxDeposit && <>
                                <Button variant={'primary'}
                                        onClick={onRespawnClickHandler}
                                        size={'lg'}
                                        disabled={!selectedAccount.isAvailable}
                                        className={'mt-3 w-100'}
                                >{getLangPath('bonus.new_life.respawn_button')}
                                </Button>
                            </>
                        }
                        {
                            (!!selectedAccount.maxDeposit && !selectedAccount.isAvailable) && <>
                                <Button variant={'secondary'}
                                        className={'mt-2 w-100'}
                                        onClick={onGoTradingClickHandler}
                                >{getLangPath('bonus.new_life.go_trading')}
                                </Button>
                            </>
                        }
                    </FormGroup>
                </>
            }
        </RespawnRealBottomStyled>

    </RespawnRealStyled>;
}
